/**
 * GPX track analysis — pure, dependency-free so it runs in the browser (map and
 * profile charts) and in the Deno edge function that stores the metrics.
 *
 * The run structure lives in `quizrun.segments`: an ordered array of
 * `{type:"run"|"think", length}` where a run length is **kilometres** and a
 * think length is a question count. The actual boundaries come from the
 * participant's submissions (`quizrun_submit.think_start/think_end`, keyed by
 * the 0-based think-segment ordinal):
 *
 *   run leg 0  : anchor (GPX start / gun time) -> think_start[0]
 *   run leg k  : think_end[k-1]                -> think_start[k]
 *   trailing   : think_end[last]               -> last trackpoint
 *   pause k    : think_start[k]                -> think_end[k]
 *
 * Legs are matched positionally to the run segments, so leg k is validated
 * against the k-th run segment's expected distance. Pauses measure how far
 * someone moved while they were supposed to be standing still answering.
 *
 * Those boundaries are self-reported, though, and a participant can upload a
 * GPX recorded on a completely different day from the one they answered on. So
 * the legs are computed a second way that needs no submissions at all: walk
 * each run's configured distance and take the instant it crosses that target.
 * `distanceLegs` is that view — it answers "did they actually cover the
 * distance, and how long did it take them", independently of anything they
 * typed. A completed non-background think window restarts the next run's
 * distance counter; movement during the think window stays in its pause row.
 * Background think windows are deliberately not timing boundaries.
 */

export interface TrackPoint {
  lat: number;
  lon: number;
  /** Metres above sea level, when the export carries elevation. */
  ele: number | null;
  /** Epoch milliseconds. */
  t: number;
}

export interface RunSegmentConfig {
  type: "run" | "think";
  /** Kilometres for a run segment, question count for a think segment. */
  length: number;
  /** Background think segments may overlap running and never form run boundaries. */
  background?: boolean;
}

/** One think segment's submitted window (epoch ms, null when not submitted). */
export interface ThinkWindow {
  /** 0-based think-segment ordinal, as stored in quizrun_submit.segment_index. */
  segmentIndex: number;
  start: number | null;
  end: number | null;
}

export interface TrackLeg {
  /** 0-based run-leg ordinal, matching the k-th run segment. */
  index: number;
  expectedKm: number | null;
  startTime: string | null;
  endTime: string | null;
  distanceM: number | null;
  durationSeconds: number | null;
  movingSeconds: number | null;
  avgPaceSPerKm: number | null;
  maxSpeedKmh: number | null;
  elevationGainM: number | null;
  /** Signed percentage difference of actual vs expected distance. */
  deltaPct: number | null;
  pointCount: number;
  /** True when a boundary timestamp was missing, so the leg couldn't be measured. */
  incomplete: boolean;
}

export interface TrackPause {
  segmentIndex: number;
  startTime: string | null;
  endTime: string | null;
  durationSeconds: number | null;
  /** Path length walked during the pause. */
  driftM: number | null;
  /** Furthest distance reached from the point where the pause started. */
  maxRadiusM: number | null;
  pointCount: number;
  incomplete: boolean;
}

/**
 * A run leg cut by distance rather than by submissions. Each configured run
 * starts its own distance counter, while a standard think end starts the next
 * run's counter. The track-derived boundaries are measurable even when form
 * timestamps are unusable.
 */
export interface DistanceLeg {
  /** 0-based run-leg ordinal, matching the k-th run segment. */
  index: number;
  expectedKm: number;
  /** Cumulative configured-course target, retained for stored JSON compatibility. */
  targetM: number;
  startTime: string | null;
  /** When this run's distance target was crossed; the last trackpoint if never. */
  endTime: string | null;
  /** Distance covered after this run's start — the target once `reached`. */
  distanceM: number;
  durationSeconds: number | null;
  /** Time actually spent moving; excludes a pause that fell inside the leg. */
  movingSeconds: number | null;
  avgPaceSPerKm: number | null;
  maxSpeedKmh: number | null;
  /** False when the track never got within MILESTONE_TOLERANCE_M of the target. */
  reached: boolean;
}

export type FlagSeverity = "info" | "warning" | "error";

export interface TrackFlag {
  code: string;
  severity: FlagSeverity;
  detail: string;
}

export interface TrackAnalysis {
  startTime: string | null;
  endTime: string | null;
  startLat: number | null;
  startLon: number | null;
  totalDistanceM: number;
  elapsedSeconds: number;
  movingSeconds: number;
  elevationGainM: number | null;
  elevationLossM: number | null;
  avgPaceSPerKm: number | null;
  maxSpeedKmh: number | null;
  pointCount: number;
  legs: TrackLeg[];
  distanceLegs: DistanceLeg[];
  pauses: TrackPause[];
  flags: TrackFlag[];
}

export interface AnalyzeOptions {
  segments: RunSegmentConfig[];
  thinkWindows: ThinkWindow[];
  /** Authoritative start (result.start_time or event.gun_time), epoch ms. */
  anchor?: number | null;
}

// --- Tuning constants -------------------------------------------------------

/** Steps below this are measured against the last *accepted* point instead of
 *  being dropped, so slow walking still accumulates while standing-still GPS
 *  jitter (which oscillates around one spot) does not. */
const MIN_STEP_M = 2;
/** Below this speed a step counts as standing still, not moving time. */
const MIN_MOVING_SPEED_MS = 0.5;
/** Elevation deltas smaller than this are barometric/GPS noise. */
const MIN_ELE_STEP_M = 1;
/** Max speed is averaged over at least this long to ignore single-fix spikes. */
const SPEED_WINDOW_S = 5;
/** Sustained speed above this is not running. */
const IMPLAUSIBLE_SPEED_KMH = 25;
/** A silent stretch longer than this means the recording was paused. */
const RECORDING_GAP_S = 120;
/** Distance covered in under 10s that no runner could cover. */
const POSITION_JUMP_M = 300;
const POSITION_JUMP_S = 10;
/** Drift while "thinking" beyond these is suspicious. */
const DRIFT_WARN_M = 150;
const DRIFT_ERROR_M = 400;
/** Fractions of the configured leg distance that count as short / long. */
const LEG_SHORT_WARN = 0.9;
const LEG_SHORT_ERROR = 0.75;
const LEG_LONG_INFO = 1.25;
/** A cumulative distance target counts as reached within this margin — GPS
 *  smoothing means the last few metres of a leg are never exact. */
const MILESTONE_TOLERANCE_M = 100;
/** How far the whole course may be off before the run itself is in doubt. An
 *  uneven split (2.4 km + 2.6 km) is normal; a short *total* is not. */
const COURSE_MARGIN_M = 250;
/** Overlap shorter than this is a boundary rounding artefact, not real. */
const OVERLAP_MIN_S = 5;
/** How far a submitted window may fall outside the recording before it stops
 *  describing it. Covers stopping the watch a few minutes before submitting. */
const WINDOW_GRACE_S = 600;

const EARTH_RADIUS_M = 6_371_008.8;

/** Great-circle distance in metres. */
export function haversineM(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const toRad = Math.PI / 180;
  const dLat = (bLat - aLat) * toRad;
  const dLon = (bLon - aLon) * toRad;
  const lat1 = aLat * toRad;
  const lat2 = bLat * toRad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

const TRKPT_RE = /<trkpt\b([^>]*?)\/>|<trkpt\b([^>]*?)>([\s\S]*?)<\/trkpt>/gi;

function attr(tag: string, name: string): number {
  const m = tag.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']+)["']`, "i"));
  return m ? Number(m[1]) : NaN;
}

/**
 * Parse `<trkpt>` elements into an ordered point list. Points without valid
 * coordinates or without a timestamp are dropped — every metric here is
 * time-based, so an untimed point can't be placed on a leg.
 */
export function parseGpxPoints(xml: string): TrackPoint[] {
  const points: TrackPoint[] = [];
  TRKPT_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TRKPT_RE.exec(xml)) !== null) {
    const tag = m[1] ?? m[2] ?? "";
    const body = m[3] ?? "";
    const lat = attr(tag, "lat");
    const lon = attr(tag, "lon");
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    const timeText = body.match(/<time>\s*([^<]+?)\s*<\/time>/i)?.[1];
    if (!timeText) continue;
    const t = Date.parse(timeText);
    if (Number.isNaN(t)) continue;

    const eleText = body.match(/<ele>\s*([^<]+?)\s*<\/ele>/i)?.[1];
    const ele = eleText === undefined ? NaN : Number(eleText);

    points.push({ lat, lon, ele: Number.isFinite(ele) ? ele : null, t });
  }
  points.sort((a, b) => a.t - b.t);
  return points;
}

/**
 * The `<metadata><time>` stamp, as ISO. Only ever a fallback: a track with real
 * points is anchored on the first one, because that is when the runner moved
 * rather than when the watch wrote the file. But an export with an empty
 * `<trkseg/>` still carries this, and the start time is all the scoring needs.
 */
export function parseGpxMetadataTime(xml: string): string | null {
  const meta = xml.match(/<metadata[\s>][\s\S]*?<\/metadata>/i)?.[0];
  const text = meta?.match(/<time>\s*([^<]+?)\s*<\/time>/i)?.[1];
  if (!text) return null;
  const t = Date.parse(text);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

const iso = (ms: number | null | undefined): string | null =>
  ms == null || Number.isNaN(ms) ? null : new Date(ms).toISOString();

/** "3 days", "12 h 51 min", "25 min" — timezone-free, for flag details. */
const formatGap = (ms: number): string => {
  const minutes = Math.round(ms / 60_000);
  if (minutes >= 2 * 24 * 60) return `${Math.round(minutes / (24 * 60))} days`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
  return `${minutes} min`;
};

interface SpanMetrics {
  distanceM: number;
  durationSeconds: number;
  movingSeconds: number;
  elevationGainM: number | null;
  elevationLossM: number | null;
  maxSpeedKmh: number | null;
  maxRadiusM: number;
  pointCount: number;
}

/**
 * Single pass over the points accumulating filtered distance and moving time.
 * Shared so the totals, the per-leg metrics and the distance milestones all
 * count the same metres — a milestone found against a differently-filtered
 * distance would land at a different instant than the leg it terminates.
 */
function walk(points: TrackPoint[]): { cum: number[]; movingSeconds: number } {
  const cum = new Array<number>(points.length).fill(0);
  if (points.length === 0) return { cum, movingSeconds: 0 };

  let anchor = points[0];
  let distance = 0;
  let moving = 0;
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    const step = haversineM(anchor.lat, anchor.lon, p.lat, p.lon);
    if (step >= MIN_STEP_M) {
      const dt = (p.t - anchor.t) / 1000;
      distance += step;
      if (dt > 0 && step / dt >= MIN_MOVING_SPEED_MS) moving += dt;
      anchor = p;
    }
    cum[i] = distance;
  }
  return { cum, movingSeconds: moving };
}

/**
 * Metrics for a contiguous slice of points. Distance uses a "last accepted
 * point" filter (see MIN_STEP_M) so jitter doesn't inflate a stationary slice.
 */
function measure(points: TrackPoint[]): SpanMetrics {
  if (points.length < 2) {
    return {
      distanceM: 0,
      durationSeconds: 0,
      movingSeconds: 0,
      elevationGainM: null,
      elevationLossM: null,
      maxSpeedKmh: null,
      maxRadiusM: 0,
      pointCount: points.length,
    };
  }

  const { cum, movingSeconds } = walk(points);
  const distance = cum[cum.length - 1];

  let maxRadius = 0;
  for (let i = 1; i < points.length; i++) {
    maxRadius = Math.max(
      maxRadius,
      haversineM(points[0].lat, points[0].lon, points[i].lat, points[i].lon),
    );
  }

  // Elevation: only report when the export actually carries <ele> data.
  let gain: number | null = null;
  let loss: number | null = null;
  let lastEle: number | null = null;
  for (const p of points) {
    if (p.ele == null) continue;
    if (lastEle === null) {
      lastEle = p.ele;
      gain ??= 0;
      loss ??= 0;
      continue;
    }
    const d = p.ele - lastEle;
    if (Math.abs(d) < MIN_ELE_STEP_M) continue;
    if (d > 0) gain = (gain ?? 0) + d;
    else loss = (loss ?? 0) - d;
    lastEle = p.ele;
  }

  // Max speed over a >= SPEED_WINDOW_S window, so a single bad fix can't spike it.
  let maxSpeed: number | null = null;
  let lo = 0;
  for (let hi = 1; hi < points.length; hi++) {
    // Keep the window as tight as possible while still spanning SPEED_WINDOW_S.
    while (
      lo + 1 < hi &&
      (points[hi].t - points[lo + 1].t) / 1000 >= SPEED_WINDOW_S
    )
      lo++;
    const dt = (points[hi].t - points[lo].t) / 1000;
    if (dt < SPEED_WINDOW_S) continue;
    const speed = ((cum[hi] - cum[lo]) / dt) * 3.6;
    if (maxSpeed === null || speed > maxSpeed) maxSpeed = speed;
  }
  const elapsed = (points[points.length - 1].t - points[0].t) / 1000;
  // Tracks shorter than the smoothing window still get an average-based figure.
  if (maxSpeed === null && elapsed > 0) maxSpeed = (distance / elapsed) * 3.6;

  return {
    distanceM: distance,
    durationSeconds: elapsed,
    movingSeconds,
    elevationGainM: gain,
    elevationLossM: loss,
    maxSpeedKmh: maxSpeed,
    maxRadiusM: maxRadius,
    pointCount: points.length,
  };
}

const slice = (points: TrackPoint[], from: number, to: number): TrackPoint[] =>
  points.filter((p) => p.t >= from && p.t <= to);

function interpolatePoint(
  before: TrackPoint,
  after: TrackPoint,
  t: number,
): TrackPoint | null {
  const span = after.t - before.t;
  if (span <= 0) return null;
  const fraction = (t - before.t) / span;
  const interpolate = (a: number, b: number) => a + (b - a) * fraction;
  return {
    lat: interpolate(before.lat, after.lat),
    lon: interpolate(before.lon, after.lon),
    ele:
      before.ele !== null && after.ele !== null
        ? interpolate(before.ele, after.ele)
        : null,
    t,
  };
}

/**
 * The first point after a distance-derived boundary may be several seconds
 * later. Interpolate a point at the boundary so the next run starts with its
 * full configured distance rather than losing that partial first step.
 */
function sliceFrom(points: TrackPoint[], from: number): TrackPoint[] {
  const after = points.filter((p) => p.t >= from);
  if (after.length === 0 || after[0].t === from) return after;

  let before: TrackPoint | undefined;
  for (const point of points) {
    if (point.t >= from) break;
    before = point;
  }
  if (!before) return after;
  const next = after[0];
  const start = interpolatePoint(before, next, from);
  return start ? [start, ...after] : after;
}

/**
 * Include an interpolated endpoint when a distance milestone falls between
 * sampled points, so the final partial interval participates in its metrics.
 */
function sliceTo(points: TrackPoint[], to: number): TrackPoint[] {
  const before = points.filter((p) => p.t <= to);
  const next = points[before.length];
  if (before.length === 0 || !next || before[before.length - 1].t === to)
    return before;

  const end = interpolatePoint(before[before.length - 1], next, to);
  return end ? [...before, end] : before;
}

/** Keep partial intervals at both ends of a timestamp-bounded track span. */
function sliceBetween(
  points: TrackPoint[],
  from: number,
  to: number,
): TrackPoint[] {
  return sliceTo(sliceFrom(points, from), to);
}

const paceSPerKm = (distanceM: number, seconds: number): number | null =>
  distanceM > 0 && seconds > 0 ? seconds / (distanceM / 1000) : null;

/**
 * The instant the cumulative distance first reaches `targetM`, interpolated
 * between the two bracketing trackpoints so a 10-second fix interval doesn't
 * quantise the leg time. `reached` is false only when the track never came
 * within MILESTONE_TOLERANCE_M of the target — that is the "they never covered
 * the distance" signal, and it can't be faked by stopping the recording early.
 */
function milestone(
  points: TrackPoint[],
  cum: number[],
  targetM: number,
): { t: number; distanceM: number; reached: boolean } {
  const last = points.length - 1;
  const total = cum[last] ?? 0;

  if (total + MILESTONE_TOLERANCE_M < targetM) {
    return { t: points[last].t, distanceM: total, reached: false };
  }
  for (let i = 1; i <= last; i++) {
    if (cum[i] < targetM) continue;
    const span = cum[i] - cum[i - 1];
    const f = span > 0 ? (targetM - cum[i - 1]) / span : 0;
    return {
      t: points[i - 1].t + f * (points[i].t - points[i - 1].t),
      distanceM: targetM,
      reached: true,
    };
  }
  // Short of the target but inside the tolerance: close enough, and the run
  // ended where the recording did.
  return { t: points[last].t, distanceM: total, reached: true };
}

/** Analyse a parsed track against the quizrun's run/think configuration. */
export function analyzeTrack(
  points: TrackPoint[],
  { segments, thinkWindows, anchor }: AnalyzeOptions,
): TrackAnalysis {
  const flags: TrackFlag[] = [];

  if (points.length === 0) {
    return {
      startTime: null,
      endTime: null,
      startLat: null,
      startLon: null,
      totalDistanceM: 0,
      elapsedSeconds: 0,
      movingSeconds: 0,
      elevationGainM: null,
      elevationLossM: null,
      avgPaceSPerKm: null,
      maxSpeedKmh: null,
      pointCount: 0,
      legs: [],
      distanceLegs: [],
      pauses: [],
      flags: [
        {
          code: "no_points",
          severity: "error",
          detail: "The GPX file has no timed trackpoints.",
        },
      ],
    };
  }

  const first = points[0];
  const last = points[points.length - 1];
  const overall = measure(points);

  const windowBySegment = new Map<number, ThinkWindow>();
  for (const w of thinkWindows) windowBySegment.set(w.segmentIndex, w);

  // Walk the configured segments in order so every run leg is bound to the
  // non-background think segments that actually surround it. quizrun_submit
  // rows are created lazily (one per opened form), so the submitted set can be
  // sparse — matching legs to it by array position would silently measure a leg
  // against the wrong window, or stretch an interior leg to the end of the
  // track. Background forms can be opened while running, so their timestamps
  // must never cut a run leg.
  const runPlan: {
    expectedKm: number | null;
    /** Non-background think ordinal this leg starts after, null at the anchor. */
    prevThink: number | null;
    /** Non-background think ordinal this leg ends at, null for a trailing leg. */
    nextThink: number | null;
  }[] = [];
  let thinkCount = 0;
  let previousBoundaryThink: number | null = null;
  const backgroundThinkIndexes = new Set<number>();
  for (const seg of segments) {
    if (seg.type === "think") {
      const thinkIndex = thinkCount;
      thinkCount++;
      if (seg.background) {
        backgroundThinkIndexes.add(thinkIndex);
        continue;
      }
      const previousRun = runPlan[runPlan.length - 1];
      if (previousRun && previousRun.nextThink === null) {
        previousRun.nextThink = thinkIndex;
      }
      previousBoundaryThink = thinkIndex;
    } else if (seg.type === "run") {
      const length = Number(seg.length);
      runPlan.push({
        expectedKm: Number.isFinite(length) ? length : null,
        prevThink: previousBoundaryThink,
        nextThink: null,
      });
    }
  }

  const startMs = anchor ?? first.t;

  // --- Run legs -------------------------------------------------------------
  const legs: TrackLeg[] = runPlan.map(
    ({ expectedKm, prevThink, nextThink }, k) => {
      const from =
        prevThink === null
          ? startMs
          : (windowBySegment.get(prevThink)?.end ?? null);
      const to =
        nextThink === null
          ? last.t
          : (windowBySegment.get(nextThink)?.start ?? null);

      // A window that runs well past the recording can't be measured against it.
      // Slicing anyway is worse than saying nothing: answers submitted months
      // after the upload would silently collect the entire track and report it as
      // one enormous leg that "covered" the whole course in 107 days.
      const graceMs = WINDOW_GRACE_S * 1000;
      const measurable =
        from !== null &&
        to !== null &&
        to > from &&
        from >= first.t - graceMs &&
        to <= last.t + graceMs;

      if (!measurable) {
        return {
          index: k,
          expectedKm,
          startTime: iso(from),
          endTime: iso(to),
          distanceM: null,
          durationSeconds: null,
          movingSeconds: null,
          avgPaceSPerKm: null,
          maxSpeedKmh: null,
          elevationGainM: null,
          deltaPct: null,
          pointCount: 0,
          incomplete: true,
        };
      }

      const m = measure(slice(points, from, to));
      const durationSeconds = (to - from) / 1000;
      const expectedM = expectedKm === null ? null : expectedKm * 1000;
      return {
        index: k,
        expectedKm,
        startTime: iso(from),
        endTime: iso(to),
        distanceM: m.distanceM,
        durationSeconds,
        movingSeconds: m.movingSeconds,
        avgPaceSPerKm: paceSPerKm(m.distanceM, durationSeconds),
        maxSpeedKmh: m.maxSpeedKmh,
        elevationGainM: m.elevationGainM,
        deltaPct:
          expectedM && expectedM > 0
            ? ((m.distanceM - expectedM) / expectedM) * 100
            : null,
        pointCount: m.pointCount,
        incomplete: false,
      };
    },
  );

  // --- Distance-derived legs ------------------------------------------------
  // Independent of when the participant submitted an answer: each run needs to
  // cover its own configured distance. A completed standard think window starts
  // the next counter afresh, so wandering while thinking cannot be credited to
  // the following run. Background windows intentionally do neither.
  const runFrom = startMs > first.t && startMs < last.t ? startMs : first.t;
  const distanceLegs: DistanceLeg[] = [];
  let cumTargetM = 0;
  let fallbackStartT = runFrom;
  const isTrackTime = (t: number | null): t is number =>
    t !== null && t >= first.t && t <= last.t;
  for (const [k, plan] of runPlan.entries()) {
    const expectedKm = plan.expectedKm ?? 0;
    const expectedM = expectedKm * 1000;
    cumTargetM += expectedM;
    const submittedResume =
      plan.prevThink === null
        ? null
        : (windowBySegment.get(plan.prevThink)?.end ?? null);
    const prevT = isTrackTime(submittedResume)
      ? submittedResume
      : fallbackStartT;
    const runPoints = sliceFrom(points, prevT);
    const { cum: runCum } = walk(runPoints);

    const mark =
      runPoints.length < 2
        ? { t: prevT, distanceM: 0, reached: false }
        : milestone(runPoints, runCum, expectedM);
    const durationSeconds = (mark.t - prevT) / 1000;
    const span = measure(sliceTo(runPoints, mark.t));

    distanceLegs.push({
      index: k,
      expectedKm,
      targetM: cumTargetM,
      startTime: iso(prevT),
      endTime: iso(mark.t),
      distanceM: mark.distanceM,
      durationSeconds: durationSeconds > 0 ? durationSeconds : null,
      movingSeconds: span.movingSeconds,
      // From moving time, not elapsed: a leg that ends a few metres short of its
      // target only crosses it after the following think stop, which would
      // otherwise land the whole pause inside this leg and wreck the pace.
      avgPaceSPerKm: paceSPerKm(
        mark.distanceM,
        span.movingSeconds > 0 ? span.movingSeconds : durationSeconds,
      ),
      maxSpeedKmh: span.maxSpeedKmh,
      reached: mark.reached,
    });

    // When a standard think end is unavailable, preserve the legacy sequential
    // fallback. A known standard end always wins on the next iteration.
    fallbackStartT = mark.t;
  }

  // --- Think pauses ---------------------------------------------------------
  // One entry per *configured* think segment, so a segment that was never
  // submitted shows up as unmeasured instead of vanishing from the report.
  // Any submitted window beyond the configured count is kept too, so stale
  // data is visible rather than silently dropped.
  const pauseIndexes = [
    ...new Set([
      ...Array.from({ length: thinkCount }, (_, i) => i),
      ...thinkWindows.map((w) => w.segmentIndex),
    ]),
  ].sort((a, b) => a - b);

  const pauses: TrackPause[] = pauseIndexes.map((segmentIndex) => {
    const w = windowBySegment.get(segmentIndex);
    const start = w?.start ?? null;
    const end = w?.end ?? null;
    if (start === null || end === null || end <= start) {
      return {
        segmentIndex,
        startTime: iso(start),
        endTime: iso(end),
        durationSeconds: null,
        driftM: null,
        maxRadiusM: null,
        pointCount: 0,
        incomplete: true,
      };
    }
    if (start < first.t || end > last.t) {
      return {
        segmentIndex,
        startTime: iso(start),
        endTime: iso(end),
        durationSeconds: null,
        driftM: null,
        maxRadiusM: null,
        pointCount: 0,
        incomplete: true,
      };
    }
    const m = measure(sliceBetween(points, start, end));
    return {
      segmentIndex,
      startTime: iso(start),
      endTime: iso(end),
      durationSeconds: (end - start) / 1000,
      driftM: m.distanceM,
      maxRadiusM: m.maxRadiusM,
      pointCount: m.pointCount,
      incomplete: false,
    };
  });

  // --- Validation flags -----------------------------------------------------
  // Answers submitted on another day (test submits, the wrong GPX) make every
  // leg and pause unmeasured. Say so once, plainly, instead of leaving only a
  // list of "unmeasured" flags and an all-grey route to puzzle over.
  const graceMs = WINDOW_GRACE_S * 1000;
  const submitted = thinkWindows.filter(
    (w): w is ThinkWindow & { start: number; end: number } =>
      w.start !== null && w.end !== null && w.end > w.start,
  );
  if (
    submitted.length > 0 &&
    submitted.every(
      (w) => w.end < first.t - graceMs || w.start > last.t + graceMs,
    )
  ) {
    const latestEnd = Math.max(...submitted.map((w) => w.end));
    const earliestStart = Math.min(...submitted.map((w) => w.start));
    const where =
      latestEnd < first.t
        ? ` — they end ${formatGap(first.t - latestEnd)} before the recording starts`
        : earliestStart > last.t
          ? ` — they start ${formatGap(earliestStart - last.t)} after the recording ends`
          : "";
    flags.push({
      code: "submissions_outside_track",
      severity: "warning",
      detail: `None of the submitted answer times fall within this recording${where}. Wrong GPX file, or answers submitted on another day?`,
    });
  }

  // The whole course first: an uneven split (2.4 km + 2.6 km) still means the
  // distance was run, so the total is judged strictly and the individual legs
  // leniently. Only a short *total* puts the run itself in doubt.
  const expectedCourseM = runPlan.reduce(
    (sum, p) => sum + (p.expectedKm ?? 0) * 1000,
    0,
  );
  const courseDiffM = overall.distanceM - expectedCourseM;
  const courseOnTarget =
    expectedCourseM === 0 || Math.abs(courseDiffM) <= COURSE_MARGIN_M;

  if (expectedCourseM > 0 && courseDiffM < -COURSE_MARGIN_M) {
    flags.push({
      code: "course_short",
      severity: courseDiffM < -expectedCourseM * 0.1 ? "error" : "warning",
      detail: `Track covers ${(overall.distanceM / 1000).toFixed(2)} km of the ${(expectedCourseM / 1000).toFixed(2)} km course — ${Math.round(-courseDiffM)} m short.`,
    });
  } else if (expectedCourseM > 0 && courseDiffM > COURSE_MARGIN_M) {
    flags.push({
      code: "course_long",
      severity: "info",
      detail: `Track covers ${(overall.distanceM / 1000).toFixed(2)} km, ${Math.round(courseDiffM)} m more than the ${(expectedCourseM / 1000).toFixed(2)} km course.`,
    });
  }

  for (const leg of distanceLegs) {
    if (leg.reached) continue;
    flags.push({
      code: "distance_not_reached",
      severity: "error",
      detail: `Run ${leg.index + 1} never reached its ${leg.expectedKm} km — the track ends after ${(leg.distanceM / 1000).toFixed(2)} km of it.`,
    });
  }

  for (const leg of legs) {
    const n = leg.index + 1;
    if (leg.incomplete) {
      flags.push({
        code: "leg_unmeasured",
        severity: "warning",
        detail: `Run ${n} couldn't be measured — its submitted window is missing or falls outside the recorded track.`,
      });
      continue;
    }
    if (leg.expectedKm === null || leg.distanceM === null) continue;
    const expectedM = leg.expectedKm * 1000;
    const ratio = leg.distanceM / expectedM;
    const actualKm = (leg.distanceM / 1000).toFixed(2);
    // Within the margin the split is simply how they paced it, not a finding.
    if (Math.abs(leg.distanceM - expectedM) <= MILESTONE_TOLERANCE_M) continue;

    if (ratio < LEG_SHORT_WARN) {
      flags.push({
        code: "leg_short",
        // The distance was still covered overall, so this is a split problem.
        severity:
          !courseOnTarget && ratio < LEG_SHORT_ERROR ? "error" : "warning",
        detail: `Run ${n} covered ${actualKm} km of the expected ${leg.expectedKm} km${courseOnTarget ? ", though the full course distance was covered" : ""}.`,
      });
    } else if (ratio > LEG_LONG_INFO) {
      flags.push({
        code: "leg_long",
        severity: "info",
        detail: `Run ${n} covered ${actualKm} km, more than the expected ${leg.expectedKm} km.`,
      });
    }
  }

  for (const pause of pauses) {
    // Background answers are expected while moving or may be intentionally
    // unsubmitted, so they never participate in pause validation.
    if (backgroundThinkIndexes.has(pause.segmentIndex)) continue;

    const n = pause.segmentIndex + 1;
    if (pause.incomplete) {
      const hasWindow =
        pause.startTime !== null &&
        pause.endTime !== null &&
        Date.parse(pause.endTime) > Date.parse(pause.startTime);
      flags.push({
        code: "pause_unmeasured",
        severity: "warning",
        detail: hasWindow
          ? `Think stop ${n}'s submitted window falls outside the recorded track.`
          : `Think stop ${n} has no submitted start/finish time.`,
      });
      continue;
    }

    if (pause.startTime === null || pause.endTime === null) continue;
    const pFrom = Date.parse(pause.startTime);
    const pTo = Date.parse(pause.endTime);

    // Checked against the track-derived run windows rather than
    // submission-derived legs, which would be circular.
    //
    // Overlap alone isn't enough to accuse anyone: a leg that ends a few metres
    // short of its target only crosses it after the pause, so an honest stop
    // sits inside the leg window. What matters is whether they were still
    // covering ground, so the overlap has to contain real distance.
    for (const leg of distanceLegs) {
      if (!leg.startTime || !leg.endTime) continue;
      const from = Math.max(pFrom, Date.parse(leg.startTime));
      const to = Math.min(pTo, Date.parse(leg.endTime));
      if ((to - from) / 1000 <= OVERLAP_MIN_S) continue;

      const movedM = measure(slice(points, from, to)).distanceM;
      if (movedM <= MILESTONE_TOLERANCE_M) continue;

      flags.push({
        code: "think_during_run",
        severity: "error",
        detail: `Think stop ${n} overlaps run ${leg.index + 1} for ${Math.round((to - from) / 1000)}s, covering ${Math.round(movedM)} m of the course while answering.`,
      });
    }

    if (pause.driftM === null) continue;
    if (pause.driftM > DRIFT_WARN_M) {
      flags.push({
        code: "think_drift",
        severity: pause.driftM > DRIFT_ERROR_M ? "error" : "warning",
        detail: `Moved ${Math.round(pause.driftM)} m during think stop ${n}.`,
      });
    }
  }

  if (
    overall.maxSpeedKmh !== null &&
    overall.maxSpeedKmh > IMPLAUSIBLE_SPEED_KMH
  ) {
    flags.push({
      code: "implausible_speed",
      severity: "error",
      detail: `Sustained ${overall.maxSpeedKmh.toFixed(1)} km/h — too fast to be running.`,
    });
  }

  for (let i = 1; i < points.length; i++) {
    const gapS = (points[i].t - points[i - 1].t) / 1000;
    const jumpM = haversineM(
      points[i - 1].lat,
      points[i - 1].lon,
      points[i].lat,
      points[i].lon,
    );
    if (gapS > RECORDING_GAP_S) {
      flags.push({
        code: "recording_gap",
        severity: "warning",
        detail: `${Math.round(gapS)}s gap in the recording (${Math.round(jumpM)} m apart) at ${iso(points[i - 1].t)}.`,
      });
    } else if (jumpM > POSITION_JUMP_M && gapS <= POSITION_JUMP_S) {
      flags.push({
        code: "position_jump",
        severity: "error",
        detail: `Jumped ${Math.round(jumpM)} m in ${Math.round(gapS)}s at ${iso(points[i - 1].t)}.`,
      });
    }
  }

  if (anchor != null && (anchor < first.t - 60_000 || anchor > last.t)) {
    flags.push({
      code: "anchor_outside_track",
      severity: "warning",
      detail: `The recorded start (${iso(anchor)}) falls outside the track (${iso(first.t)} – ${iso(last.t)}).`,
    });
  }

  return {
    startTime: iso(first.t),
    endTime: iso(last.t),
    startLat: first.lat,
    startLon: first.lon,
    totalDistanceM: overall.distanceM,
    elapsedSeconds: overall.durationSeconds,
    movingSeconds: overall.movingSeconds,
    elevationGainM: overall.elevationGainM,
    elevationLossM: overall.elevationLossM,
    avgPaceSPerKm: paceSPerKm(overall.distanceM, overall.durationSeconds),
    maxSpeedKmh: overall.maxSpeedKmh,
    pointCount: points.length,
    legs,
    distanceLegs,
    pauses,
    flags,
  };
}
